/**
 * Klient API. Access token żyje wyłącznie w pamięci — nie w localStorage, skąd
 * mógłby go odczytać dowolny skrypt na stronie (SECURITY.md §2). Odświeżanie opiera
 * się na cookie HttpOnly, którego JavaScript nie widzi.
 */

export interface ApiError {
  readonly code: string;
  readonly message: string;
  readonly correlationId?: string;
  readonly details?: Record<string, unknown>;
}

export class ApiRequestError extends Error {
  constructor(readonly error: ApiError, readonly status: number) {
    super(error.message);
    this.name = "ApiRequestError";
  }
}

let accessToken: string | null = null;
let refreshing: Promise<boolean> | null = null;

export function setAccessToken(token: string | null): void {
  accessToken = token;
}

export function hasAccessToken(): boolean {
  return accessToken !== null;
}

interface RequestOptions {
  method?: string;
  body?: unknown;
  /** Wyłącza automatyczne odświeżenie — używane przez samo odświeżanie, żeby nie zapętlić. */
  skipRefresh?: boolean;
}

export async function api<T>(path: string, options: RequestOptions = {}): Promise<T> {
  const response = await send(path, options);

  // Jedna próba odświeżenia sesji; równoległe żądania czekają na ten sam wynik,
  // inaczej pięć zapytań na starcie ekranu wywołałoby pięć rotacji tokenu naraz.
  if (response.status === 401 && !options.skipRefresh) {
    const refreshed = await refreshSession();
    if (refreshed) {
      const retried = await send(path, options);
      return parse<T>(retried);
    }
  }

  return parse<T>(response);
}

async function send(path: string, options: RequestOptions): Promise<Response> {
  return fetch(path, {
    method: options.method ?? "GET",
    headers: {
      ...(options.body ? { "content-type": "application/json" } : {}),
      ...(accessToken ? { authorization: `Bearer ${accessToken}` } : {}),
    },
    ...(options.body ? { body: JSON.stringify(options.body) } : {}),
    credentials: "include",
  });
}

async function parse<T>(response: Response): Promise<T> {
  if (response.status === 204) return undefined as T;

  const payload = (await response.json().catch(() => null)) as { error?: ApiError } | T | null;

  if (!response.ok) {
    const error = (payload as { error?: ApiError } | null)?.error ?? {
      code: "internal_error",
      message: "Unexpected error",
    };
    throw new ApiRequestError(error, response.status);
  }

  return payload as T;
}

export async function refreshSession(): Promise<boolean> {
  refreshing ??= (async () => {
    try {
      const response = await fetch("/v1/auth/refresh", {
        method: "POST",
        headers: { "content-type": "application/json" },
        body: "{}",
        credentials: "include",
      });
      if (!response.ok) return false;
      const data = (await response.json()) as { accessToken: string };
      accessToken = data.accessToken;
      return true;
    } catch {
      return false;
    } finally {
      // Zwolnienie dopiero po zakończeniu, żeby kolejne żądania startowały od nowa.
      queueMicrotask(() => {
        refreshing = null;
      });
    }
  })();

  return refreshing;
}
